/**
 * Helper to convert size guide objects into a list of requestable guide strings.
 * @param {object[]} guides Array of guide objects
 * @param {string} guides.division Division name
 * @param {array<string>} guides.categories List of category names
 * @returns {String} Size guide API requestable string
 */
export const flattenGuidesToList = (guides) => {
  if (!guides || !Array.isArray(guides) || !guides.length) return [];
  return guides
    .reduce(
      (result, group) =>
        result.push(
          group.categories.map((category) => `${group.division}:${category}`)
        ) && result,
      []
    )
    .flat();
};

/**
 * This helper function is meant to navigate through a tree that
 * has been generated in a set way using our QBS shopify app. It
 * is accessed sequentially by an accessor array of keys, in which 
 * each key corresponds to a level of depth in the tree. If the key 
 * is not found, the tree is also checked for 'none', which indicates
 * that we dont care about that particular level of logic to determine
 * the next. example: we care about category for all socks, but also want
 * to define socks with endUse = everyday differently an endUse of
 * 'everyday' would move to the proper node, but all other nodes would
 * navigate through 'none'
 * @param {object} decisionTreeNode a tree generated for decision logic in QBS of varying depth + keys
 * @param {array<string>} accessorArray an array of keys in the tree
 * @returns {number} the quantity associated with the node navigated to by the accessorArray, or 0 by default
 */
const deepAccessQBSQuantity = (decisionTreeNode, accessorArray) => {
  const ref = accessorArray.shift();
  if (accessorArray.length === 0) {
      return decisionTreeNode?.[ref]?.quantity || decisionTreeNode?.none?.quantity || 0;
  } 
  if (decisionTreeNode?.[ref]) return deepAccessQBSQuantity(decisionTreeNode[ref], accessorArray);
  return deepAccessQBSQuantity(decisionTreeNode?.none, accessorArray);
}

/**
 * Retrieves the display value for the discount messaging
 * the category if grouped by category
 * or Items for a normal discount
 * @param {string} groupBy what the discount is grouped by
 * @param {string} category the item's category
 * @returns the capitalized value to display
 */
const getCategoryDisplayName = (groupBy, category) => {
  if (groupBy !== 'category') return 'Items';
  switch (category) {
    case 'T-SHIRTS':
      // Note, the hyphen in the returned display value is significant as it is a non-breaking hyphen.
      return 'T‑Shirts';
    // Bralettes should be messaged as underwear
    case 'BRALETTES':
    case 'PLUNGE BRALETTES':
      return 'Underwear';
    default:
      return category.charAt(0).toUpperCase() + category.slice(1).toLowerCase()
  }
}

/**
 * returns the specific product savings using attribute logic for singles QBS
 * @param {string} category category of the variant - currently sock, underwear, bralette
 * @param {Object} pimData component data - enduse, primarymaterial
 * @param {string} sku variant sku
 * @param {number} itemCount number of items in the variant (packs vs single)
 * @param {Object} qbsDiscountData object containing qbs discount data - includedskus, excludedskus, decisiontree, groupBy, discounttier
 * @returns {Object} object containing quantity needed to qualify for QBS + the % off for meeting the threshold
 */
export const fetchSavings = (
  category,
  pimData,
  sku,
  itemCount,
  qbsDiscountData
) => {
  /**
   * returns null if:
   * 1. category is undefined or null
   * 2. data from qbsDiscount query is undefined or null
   * 3. current variant is a pack
   */
  if (!category || !qbsDiscountData || itemCount > 1) {
    return null;
  }

  const tieredDiscountData = qbsDiscountData.reduce((acc, discount) => {
    // TODO: update name mappings when we swap from `parsedObject` to `object`
    const {
      excludedSkus,
      discountTier,
      parsedDecisionTree: decisionTree,
      parsedIncludedSkus: includedSkus,
      decisionKeys,
      groupBy = 'productId'
    } = discount;
    if (!decisionTree || excludedSkus.includes(sku)) {
      return acc;
    }

    /** assume CSV is uploaded and formatted properly (sku, quantity)
     * skip other logic if we manually included the variant SKU */
    if (includedSkus && includedSkus[sku]) {
      acc.push({
        quantity: includedSkus[sku],
        discount: discountTier,
        displayName: getCategoryDisplayName(groupBy, category)
      });
      return acc;
    }

    // constructs an object we can properly access since QBS dependencies arent 1:1 with product data
    const decisionKeysPimData = {
      ...pimData,
      category,
      material: pimData.primaryMaterial
    };

    
    /** construct an array of 'accessor' properties
     * based on the keys that exist in the decision tree */
    const accessorArray = [];
    decisionKeys.forEach((key) => {
      if (decisionKeysPimData[key]) accessorArray.push(decisionKeysPimData[key]?.toLowerCase());
      else accessorArray.push('none');
    });

    const discountQuantity = deepAccessQBSQuantity(decisionTree, accessorArray);
    if (discountQuantity > 0) {
      acc.push({
        quantity: discountQuantity,
        discount: discountTier,
        displayName: getCategoryDisplayName(groupBy, category)
      });
    }

    return acc;
  }, []);

  if (tieredDiscountData.length < 1) {
    return null;
  }

  return tieredDiscountData;
};

export default {};
